/**
 * One upgrade, read the same way everywhere (a city or building level, a
 * unit type's upgrade): current → next in the header, a before → after row
 * per stat with a delta chip, requirement chips (met in the ok colour, never
 * cut off: they wrap), what it Enables, the own units it Affects, the cost
 * against the stock and the action button. All content comes from an
 * `UpgradePreview` (view/upgradePreview.ts).
 *
 * Hovering the card, or one of its Affects chips, points at those units
 * through `onHighlight`: the match rings them on the map and the Units list
 * marks them (`ui.highlightIds`).
 *
 * In progress: a future owner-only timed upgrade passes `phase="inProgress"`
 * and a `progress` node (a bar, a countdown, a cancel button); it takes the
 * place of the action button, and the diff above keeps showing what the
 * upgrade will give. Nothing here runs a timer.
 */
import { type ReactNode, useEffect, useRef } from 'react';
import type { DiffRow, UpgradePreview } from '@/game/view/upgradePreview';
import { VERBS } from '@/game/view/verbs';
import { Cost } from './Cost';
import { VerbLabel } from './VerbLabel';

export interface UpgradeCardProps {
  preview: UpgradePreview;
  /** The viewer's stock, to tint what the cost lacks (with the card's own action row). */
  stock?: Readonly<Record<string, number>>;
  /** The action button: Level up / Upgrade (not needed with `externalAction`). */
  onConfirm?: () => void;
  /**
   * The panel shows the action row itself, pinned in its footer
   * (`UpgradeActionRow`): the card draws only the details.
   */
  externalAction?: boolean;
  /** Own units to point out (empty: none). */
  onHighlight?: (ids: readonly string[]) => void;
  /** `inProgress` swaps the action button for `progress`. */
  phase?: 'available' | 'inProgress';
  /** The in-progress slot (see the header comment). */
  progress?: ReactNode;
}

const TREND_CLASS: Record<DiffRow['trend'], string> = {
  up: 'text-ok',
  down: 'text-warn',
  changed: 'text-ok',
};

function Diff({ rows }: { rows: readonly DiffRow[] }) {
  if (rows.length === 0) return <p className="text-fg-subtle">No stat changes</p>;
  return (
    <dl className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto_auto] items-baseline gap-x-1.5 gap-y-0.5 text-xs tabular-nums">
      {rows.map((r) => (
        <div key={r.key} className="contents">
          <dt className="min-w-0 text-fg-muted">{r.label}</dt>
          <dd className="text-right text-fg-subtle">{r.before}</dd>
          <dd className="text-fg-subtle" aria-hidden="true">
            →
          </dd>
          <dd className={`text-right font-medium ${TREND_CLASS[r.trend]}`}>{r.after}</dd>
          <dd className={`min-w-8 text-right text-xs ${TREND_CLASS[r.trend]}`}>{r.delta ?? ''}</dd>
        </div>
      ))}
    </dl>
  );
}

export function UpgradeCard({
  preview,
  stock,
  onConfirm,
  externalAction = false,
  onHighlight,
  phase = 'available',
  progress,
}: UpgradeCardProps) {
  const allIds = [...new Set(preview.affects.flatMap((g) => g.ids))];
  // Never leave rings behind when the card goes away (not on every render:
  // the parent's callback may be new each time).
  const highlight = useRef(onHighlight);
  highlight.current = onHighlight;
  useEffect(() => () => highlight.current?.([]), []);
  const { from, to } = preview;
  const verbLabel = VERBS[preview.verb].label;

  return (
    <section
      aria-label={`${verbLabel}: ${from.name} to ${to.name}`}
      onMouseEnter={() => onHighlight?.(allIds)}
      onMouseLeave={() => onHighlight?.([])}
      className="@container flex flex-col gap-2 rounded-xl bg-fill p-2.5 text-xs"
    >
      <h3 className="flex flex-wrap items-baseline gap-x-1.5 text-sm font-medium">
        <span className="text-fg-muted">
          {from.name}
          {from.tag && <span className="ml-1 text-xs text-fg-subtle">{from.tag}</span>}
        </span>
        <span className="text-fg-subtle" aria-hidden="true">
          →
        </span>
        <span>
          {to.name}
          {to.tag && <span className="ml-1 text-xs text-fg-subtle">{to.tag}</span>}
        </span>
      </h3>

      <div className="grid gap-x-4 gap-y-2 @lg:grid-cols-2">
        <Diff rows={preview.rows} />
        <div className="flex min-w-0 flex-col gap-1.5">
          {preview.requirements.length > 0 && (
            <ul className="flex flex-wrap gap-1" aria-label="Requirements">
              {preview.requirements.map((r) => (
                <li
                  key={r.label}
                  className={`chip tabular-nums ${r.met ? 'text-ok' : 'text-warn'}`}
                >
                  <span aria-hidden="true">{r.met ? '✓' : '✗'}</span>
                  <span className="sr-only">{r.met ? 'Met: ' : 'Not met: '}</span>
                  {r.label}
                </li>
              ))}
            </ul>
          )}
          {preview.enables.length > 0 && (
            <p>
              <span className="text-fg-subtle">Enables </span>
              <span className="text-accent">{preview.enables.join(' · ')}</span>
            </p>
          )}
          {preview.affects.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-fg-subtle">Affects</span>
              {preview.affects.map((g) => (
                <button
                  key={g.label}
                  type="button"
                  onMouseEnter={() => onHighlight?.(g.ids)}
                  onMouseLeave={() => onHighlight?.(allIds)}
                  onFocus={() => onHighlight?.(g.ids)}
                  onBlur={() => onHighlight?.(allIds)}
                  className={`chip tabular-nums transition hover:bg-fill-hover ${g.held ? 'text-warn' : ''}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {!externalAction && onConfirm && (
        <UpgradeActionRow
          preview={preview}
          stock={stock}
          onConfirm={onConfirm}
          phase={phase}
          progress={progress}
        />
      )}
    </section>
  );
}

/**
 * The action row: why it can't go ahead (wrapping, never cut off), then the
 * button with the verb, the count and `<Cost>`. In progress, `progress`
 * takes the button's place.
 */
export function UpgradeActionRow({
  preview,
  stock,
  onConfirm,
  phase = 'available',
  progress,
}: Pick<UpgradeCardProps, 'preview' | 'stock' | 'phase' | 'progress'> & {
  onConfirm: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {phase === 'inProgress' ? (
        <div className="min-w-0 flex-1" data-slot="upgrade-progress">
          {progress}
        </div>
      ) : (
        <>
          {!preview.ok && <p className="min-w-0 flex-1 basis-40 text-warn">{preview.reason}</p>}
          <button
            type="button"
            disabled={!preview.ok}
            onClick={onConfirm}
            className="btn btn-secondary ml-auto shrink-0 text-xs"
          >
            <VerbLabel verb={preview.verb} />
            {preview.count > 1 && <span className="tabular-nums"> ×{preview.count}</span>}{' '}
            <Cost cost={preview.cost} stock={stock} />
          </button>
        </>
      )}
    </div>
  );
}
